/**
 * Shared `GET /api/cart` reader — single boundary-parse point for the
 * enriched cart payload. Centralised so every cart consumer (CartProvider,
 * useAddToCart post-write refetch, useCheckoutSession) hits the same URL
 * shape, the same cache-control headers, and runs the same
 * `CartResponseSchema` against the wire body via `parseApiResponse`. After
 * this helper returns, downstream callers consume `CartResponse` (typed
 * `CartLine[]`) and map to their own display shape — they MUST NOT re-parse
 * or branch on raw JSON.
 *
 * Why centralised: three inline `fetch('/api/cart?...')` sites had drifted
 * on cache headers and error handling, and each cast the body to `any[]`
 * before mapping — a renamed/dropped field on the server (e.g. the
 * `paypalCapabilities` rename in PR #253) was invisible until UX broke.
 * One helper means one parse, one error vocabulary, one place to add a
 * future `signal` / retry policy.
 *
 * Transport notes:
 *  - Query params mirror the existing inline call sites: `userId` when the
 *    buyer is authenticated, `guestId` otherwise. The route itself derives
 *    cart identity from cookies/session as well, but every existing site
 *    has been passing the explicit pair; we preserve that.
 *  - `Cache-Control: no-cache` + `Pragma: no-cache` headers match the
 *    existing sites — cart freshness is load-bearing for stock/availability.
 *  - `signal` is optional; not all current consumers thread an
 *    AbortController, but the master CartProvider has a `loadTokenRef`
 *    cancellation pattern that may want to evolve into one.
 *  - Non-2xx → throws `Error('Cart API response not ok: <status>')` — same
 *    string CartProvider was already throwing, so existing `.catch` blocks
 *    keep working without translation.
 *
 * @see packages/contract/src/cart.ts — CartResponseSchema (boundary contract)
 * @see packages/contract/src/api-client.ts — parseApiResponse
 * @see app/api/cart/route.ts — server emitter (single source of truth)
 */

import {
  parseApiResponse,
  CartResponseSchema,
  type CartResponse,
} from "@kraahl/contract";

export interface FetchCartLinesArgs {
  /** Authenticated buyer id. Mutually exclusive with `guestId` at the URL level. */
  userId?: string | null;
  /** Guest cart id (from `getGuestId()`) when the buyer is not authenticated. */
  guestId?: string | null;
  /** Optional abort signal — threaded into `fetch`. */
  signal?: AbortSignal;
}

/**
 * Fetch the buyer's cart from `GET /api/cart` and return it boundary-parsed
 * as `CartResponse`. Throws on non-2xx or on schema mismatch
 * (`ApiResponseParseError` from `@kraahl/contract`). Does NOT swallow.
 */
export async function fetchCartLines(
  args: FetchCartLinesArgs,
): Promise<CartResponse> {
  const params = new URLSearchParams();
  if (args.userId) {
    params.set("userId", args.userId);
  } else if (args.guestId) {
    params.set("guestId", args.guestId);
  }

  const url = params.toString()
    ? `/api/cart?${params.toString()}`
    : "/api/cart";

  const res = await fetch(url, {
    headers: { "Cache-Control": "no-cache", Pragma: "no-cache" },
    ...(args.signal ? { signal: args.signal } : {}),
  });

  if (!res.ok) {
    throw new Error(`Cart API response not ok: ${res.status}`);
  }

  return parseApiResponse(res, CartResponseSchema);
}
